import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getAlignCenterControl } from 'owa-compose-formatcontrols/lib/components/alignCenter';
import { getAlignLeftControl } from 'owa-compose-formatcontrols/lib/components/alignLeft';
import { getAlignRightControl } from 'owa-compose-formatcontrols/lib/components/alignRight';
import { getBoldControl } from 'owa-compose-formatcontrols/lib/components/bold';
import { getBulletsControl } from 'owa-compose-formatcontrols/lib/components/bullets';
import { getChangeCaseControl } from 'owa-compose-formatcontrols/lib/components/changeCase';
import { getCopyControl } from 'owa-compose-formatcontrols/lib/components/copy';
import { getCutControl } from 'owa-compose-formatcontrols/lib/components/cut';
import { getFontBackColorPickerControl } from 'owa-compose-formatcontrols/lib/components/fontBackColorPicker';
import { getFontColorPickerControl } from 'owa-compose-formatcontrols/lib/components/fontColorPicker';
import { getFontNamePickerControl } from 'owa-compose-formatcontrols/lib/components/fontNamePicker';
import { getFontSizePickerControl } from 'owa-compose-formatcontrols/lib/components/fontSizePicker';
import { getFormatPainterControl } from 'owa-compose-formatcontrols/lib/components/formatPainter';
import { getIndentControl } from 'owa-compose-formatcontrols/lib/components/indent';
import { getItalicControl } from 'owa-compose-formatcontrols/lib/components/italic';
import { getNumberingControl } from 'owa-compose-formatcontrols/lib/components/numbering';
import { getOutdentControl } from 'owa-compose-formatcontrols/lib/components/outdent';
import { getPasteControl } from 'owa-compose-formatcontrols/lib/components/paste';
import { getRemoveFormatControl } from 'owa-compose-formatcontrols/lib/components/removeFormat';
import { getStrikeThroughControl } from 'owa-compose-formatcontrols/lib/components/strikethrough';
import { getSubscriptControl } from 'owa-compose-formatcontrols/lib/components/subscript';
import { getSuperscriptControl } from 'owa-compose-formatcontrols/lib/components/superscript';
import { getUnderlineControl } from 'owa-compose-formatcontrols/lib/components/underline';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import type { RibbonControlId } from 'owa-ribbon-ids';
import { FormatControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';

/**
 * This function supplies a mapping from MailRibbonId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getMLRFormatTabControlDefinition = computedFn(
    (
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps | undefined => {
        const viewState: ComposeViewState | undefined =
            props.popoutComposeViewState ?? getComposeViewState();
        if (!viewState) {
            return undefined;
        }
        const defaultStyles = getDefaultRibbonStyles();
        switch (controlId) {
            case FormatControlId.Bold:
                return getBoldControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Italic:
                return getItalicControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Underline:
                return getUnderlineControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.StrikeThrough:
                return getStrikeThroughControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Bullets:
                return getBulletsControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Numbering:
                return getNumberingControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.AlignLeft:
                return getAlignLeftControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.AlignCenter:
                return getAlignCenterControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.AlignRight:
                return getAlignRightControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Cut:
                return getCutControl(
                    viewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format,
                    undefined
                );
            case FormatControlId.Copy:
                return getCopyControl(
                    viewState,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format,
                    undefined
                );
            case FormatControlId.Paste:
                return getPasteControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.ChangeCase:
                return getChangeCaseControl(
                    viewState.editorId,
                    defaultStyles,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Subscript:
                return getSubscriptControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Superscript:
                return getSuperscriptControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Indent:
                return getIndentControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.Outdent:
                return getOutdentControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.FontColorPicker:
                return getFontColorPickerControl(
                    viewState.editorId,
                    viewState.ribbon,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.FontBackColorPicker:
                return getFontBackColorPickerControl(
                    viewState.editorId,
                    viewState.ribbon,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.FontNamePicker:
                return getFontNamePickerControl(
                    viewState.editorId,
                    viewState.ribbon,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.FontSizePicker:
                return getFontSizePickerControl(
                    viewState.editorId,
                    viewState.ribbon,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.FormatPainter:
                return getFormatPainterControl(
                    viewState.editorId,
                    viewState.ribbon,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            case FormatControlId.RemoveFormat:
                return getRemoveFormatControl(
                    viewState.editorId,
                    defaultStyles,
                    MailRibbonTabId.Tab_Format
                );
            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getMLRFormatTabControlDefinition'
                );
        }
    }
);
